<template>
  <div class="flex flex-col gap-6 z-1000 w-full h-full">
    <loading-overlay v-if="loading" :value="loading" />
    <div v-else class="flex w-full items-start justify-center">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div class="flex items-center gap-2">
            <DropdownMenu v-if="uuid && availableDownloadLanguages.length">
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="icon">
                  <svg-icon
                    name="huge-download"
                    class="w-4 h-4 text-foreground"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  v-for="item in availableDownloadLanguages"
                  :key="item.lang"
                  @select="handleDownloadCommand(item.lang)"
                >
                  {{ item.label }}
                </DropdownMenuItem>
                <template v-if="availableDownloadLanguages.length > 1">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem @select="handleDownloadCommand('all')">
                    {{ $t("Скачать все языки") }}
                  </DropdownMenuItem>
                </template>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu v-if="uuid && availableDownloadLanguages.length">
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="icon">
                  <svg-icon name="huge-print" class="w-4 h-4 text-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  v-for="item in availableDownloadLanguages"
                  :key="item.lang"
                  @select="handlePrintCommand(item.lang)"
                >
                  {{ item.label }}
                </DropdownMenuItem>
                <template v-if="availableDownloadLanguages.length > 1">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem @select="handlePrintCommand('all')">
                    {{ $t("Печать всех языков") }}
                  </DropdownMenuItem>
                </template>
              </DropdownMenuContent>
            </DropdownMenu>
            <div v-if="!execView" class="flex items-center gap-2">
              <el-tooltip :content="$t('Режим просмотра')" placement="top">
                <Button variant="outline" size="icon" @click="goToView">
                  <svg-icon name="huge-eye" class="w-4 h-4 text-foreground" />
                </Button>
              </el-tooltip>
              <Button
                v-if="form.status === StatusCode.PROJECT"
                variant="default"
                @click="submitForm"
                :disabled="loading"
                >{{ $t("Сохранить") }}</Button
              >
              <DropdownMenu>
                <DropdownMenuTrigger as-child :disabled="loading">
                  <Button variant="outline" class="gap-2" :disabled="loading">
                    <StatusDot :code="form.status" />
                    <div>
                      {{ getStatusLabel(form.status) || form.status }}
                    </div>
                    <svg-icon
                      name="huge-shevron-down"
                      class="ml-0.5 w-4 h-4 text-muted-foreground"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <template v-if="statusOptions.length">
                    <DropdownMenuItem
                      v-for="item in statusOptions"
                      :key="item.value"
                      :disabled="item.value === form.status"
                      @select="handleChangeStatus(item.value)"
                    >
                      <div class="flex items-center gap-2">
                        <StatusDot :code="item.value" />
                        <div>{{ item.label }}</div>
                      </div>
                    </DropdownMenuItem>
                  </template>
                  <DropdownMenuItem v-else disabled>
                    <span class="text-muted-foreground">
                      {{ $t("Нет доступных действий") }}
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div v-else class="flex items-center gap-3">
              <Button variant="outline" size="icon" @click="goToEdit">
                <svg-icon name="huge-edit" class="w-4 h-4 text-foreground" />
              </Button>
            </div>
          </div>
        </template>
      </BaseTabs>
    </div>

    <div ref="formContainerRef" class="relative flex w-full flex-1 min-h-0">
      <Stepper
        v-model:open="stepperOpen"
        v-model:expanded-key="expandedStepKey"
        :steps="steps"
        :done-count="doneCount"
        :total-steps="totalSteps"
        :progress="progress"
        @step-action="onStepperAction"
      />

      <component
        :is="currentTabComponent"
        :form="form"
        :template="template"
        :key="form.id"
        @insert:field="insertFieldCollectedAmount"
        @change:tab="changeTab"
        @refresh="onTabRefresh"
        @paid="onPaymentPaid"
        @request:sign="changeTab('sign')"
        ref="activeForm"
        class="flex-1 min-w-0 h-full"
      />
    </div>

    <DialogWrapper
      v-model="showStatusNoteDialog"
      v-model:content="statusNote"
      :title="$t('Укажите причину')"
      :disable-save="!statusNote.trim()"
      @close="closeStatusNoteDialog"
      @save="confirmStatusNoteDialog"
    />

    <NotaryActionPicker
      v-if="showActionPicker && pendingAction"
      :title="$t('Выберите нотариальное действие')"
      :actions="pendingAction.notary_action_codes"
      @select="onActionPickerSelect"
      @close="closeActionPicker"
    />

    <StepNavBar
      :open="navBarVisible"
      storage-key="exec-inscription-bar"
      :show-stepper="!!uuid && !execView"
      v-model:stepper-open="stepperOpen"
      :done-count="doneCount"
      :total-steps="totalSteps"
      :progress="progress"
      :has-prev="hasPrevTab"
      :has-next="hasNextTab"
      @prev="goToPrevTab"
      @next="tryGoNext"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { toast } from "@/shared/store/alertStore";
import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import ExecutiveInscriptionMain from "./tabs/ExecutiveInscriptionMain.vue";
import ExecutiveInscriptionRedactor from "./tabs/ExecutiveInscriptionRedactor.vue";
import ExecutiveInscriptionSign from "./tabs/ExecutiveInscriptionSign.vue";
import ExecutiveInscriptionAttachments from "./tabs/ExecutiveInscriptionAttachments.vue";
import { getTemplateItem } from "@/entities/template";
import ExecutiveInscriptionHistoryView from "./tabs/view/ExecutiveInscriptionHistoryView.vue";
import ExecutiveInscriptionCalculation from "./tabs/ExecutiveInscriptionCalculation.vue";
import { PaymentTab } from "@/features/payments";
import Button from "@/shared/ui/button/Button.vue";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/shared/ui/dropdown-menu";
import StepNavBar from "@/shared/ui/step-nav-bar/StepNavBar.vue";
import { useUnfilledMentions } from "@/shared/lib/composables/useUnfilledMentions";
import { useTabFlow } from "@/shared/lib/composables/useTabFlow";
import { Stepper, useStepperState } from "@/shared/ui/stepper";
import {
  createExecInscription,
  useExecInscriptionItem,
  changeStatusExecInscription,
  getExecInscriptionItem,
} from "@/entities/exec-inscription";
import { createNotaryActionFromApplication } from "@/entities/application";
import {
  createStepRun,
  generatePdfFromTipTap,
  getNotarialTariffSaved,
  getDocumentListNotarial,
  getNotarialItem,
} from "@/entities/transaction";
import {
  StatusCode,
  ensureStatusDictionary,
  getStatusLabel,
} from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { useAvailableActions } from "@/features/notarial-actions/composables/useAvailableActions";
import NotaryActionPicker from "@/features/create-action/ui/NotaryActionPicker.vue";
import DialogWrapper from "@/shared/ui/custom/components/DialogWrapper.vue";
import { CATEGORY_CODES } from "@/shared/lib/constants";

defineProps({
  references: {
    type: Array,
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const uuid = computed(() => route.params.id);
const template_code = computed(() => route.query.code);

const { form, loading, fetchExecInscription, updateExecInscriptionItem } =
  useExecInscriptionItem(uuid);

const activeTab = ref("redactor");
const activeForm = ref(null);
const template = ref(null);
const pendingFields = ref([]);
const execView = computed(() => route.name === "exec_inscription_view");

const navBarVisible = computed(
  () => !loading.value && !execView.value && form.status === StatusCode.PROJECT
);

const formContainerRef = ref(null);

// Вырезаем шумовые атрибуты (isLoading у mention, signatureVersion у signature),
// которые меняются без правки пользователя и иначе давали бы ложный dirty.
function normalizeDoc(value) {
  if (Array.isArray(value)) return value.map(normalizeDoc);
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      if (key === "attrs" && value.attrs && typeof value.attrs === "object") {
        // eslint-disable-next-line no-unused-vars
        const { isLoading, signatureVersion, ...rest } = value.attrs;
        out.attrs = rest;
      } else {
        out[key] = normalizeDoc(value[key]);
      }
    }
    return out;
  }
  return value;
}

function serializeContent() {
  return JSON.stringify({
    content: normalizeDoc(form.content ?? null),
    content_kz: normalizeDoc(form.content_kz ?? null),
    content_en: normalizeDoc(form.content_en ?? null),
  });
}

const savedContentSnapshot = ref(null);

function captureContentBaseline() {
  savedContentSnapshot.value = serializeContent();
}

const isFormDirty = computed(() => {
  if (savedContentSnapshot.value === null) return false;
  return serializeContent() !== savedContentSnapshot.value;
});

const { unfilledMentionsCount } = useUnfilledMentions(() => [
  form.content,
  form.content_kz,
  form.content_en,
]);

const availableActionsSource = computed(() => ({
  transactionId: form.id,
}));
const {
  availableActions,
  currentStatus,
  refresh: refreshAvailableActions,
} = useAvailableActions(availableActionsSource);

async function refreshStatusOnly() {
  await refreshAvailableActions();
  if (currentStatus.value) form.status = currentStatus.value;
}

const statusOptions = computed(() =>
  availableActions.value.map((a) => ({ value: a.code, label: a.label }))
);

const showStatusNoteDialog = ref(false);
const statusNote = ref("");
const pendingStatusChange = ref(null);

const showActionPicker = ref(false);
const pendingAction = ref(null);

function handleChangeStatus(code) {
  const action = availableActions.value.find((a) => a.code === code);
  if (!action) return;

  if (action.notary_action_codes.length > 0) {
    pendingAction.value = action;
    showActionPicker.value = true;
    return;
  }

  if (code === StatusCode.REJECTED) {
    statusNote.value = "";
    pendingStatusChange.value = code;
    showStatusNoteDialog.value = true;
    return;
  }

  applyStatusChange(code);
}

function closeStatusNoteDialog() {
  showStatusNoteDialog.value = false;
  statusNote.value = "";
  pendingStatusChange.value = null;
}

async function confirmStatusNoteDialog() {
  const note = (statusNote.value || "").trim();
  if (!note || !pendingStatusChange.value) return;
  const status = pendingStatusChange.value;
  showStatusNoteDialog.value = false;
  await applyStatusChange(status, note);
  statusNote.value = "";
  pendingStatusChange.value = null;
}

async function applyStatusChange(status, note) {
  loading.value = true;
  try {
    const data = await changeStatusExecInscription(uuid.value, {
      status,
      ...(note ? { note } : {}),
    });
    if (data?.success || data?.ok) {
      await refreshStatusOnly();
    }
  } finally {
    loading.value = false;
  }
}

function closeActionPicker() {
  showActionPicker.value = false;
  pendingAction.value = null;
}

async function onActionPickerSelect(selectedAction) {
  const action = pendingAction.value;
  showActionPicker.value = false;
  if (!action || !selectedAction) {
    pendingAction.value = null;
    return;
  }
  loading.value = true;
  try {
    const data = await createNotaryActionFromApplication(uuid.value, {
      target_status: action.code,
      notary_action_code: selectedAction.code,
    });
    if (data?.success || data?.ok) {
      const newId = data.id ?? data.payload?.id;
      const newUuid = data.uuid ?? data.payload?.uuid;
      if (newId) {
        const isExecInscription =
          selectedAction.category_code === CATEGORY_CODES.SOVER_EXECUT_NADPIS;
        const txData = isExecInscription
          ? await getExecInscriptionItem(newId)
          : await getNotarialItem(newId);
        const tx = txData?.payload || txData;
        if (tx?.template_uuid) {
          await createStepRun({
            notary_transaction_id: newId,
            notary_transaction_uuid: newUuid || tx.uuid,
            template_uuid: tx.template_uuid,
          });
        }
        router.push({
          name: isExecInscription
            ? "exec_inscription_edit"
            : "notarial_action_edit",
          params: { id: newId },
        });
      } else {
        await refreshStatusOnly();
      }
    }
  } finally {
    loading.value = false;
    pendingAction.value = null;
  }
}

const tabs = computed(() => {
  if (execView.value) {
    return {
      redactor: {
        label: t("Документ"),
        icon: "huge-eye",
      },
      attachments: { label: t("Вложения"), icon: "huge-file-attach" },
      payment: {
        label: t("Оплата"),
        icon: "huge-document-info",
      },
      history: {
        label: t("История"),
        icon: "huge-history",
      },
      sign: {
        label: t("Подписание"),
        icon: "huge-editor-magic-wand",
      },
    };
  }

  return {
    redactor: {
      label: t("Редактор"),
      icon: "huge-note",
    },
    main: {
      label: t("Параметры"),
      icon: "huge-text-config",
    },
    attachments: { label: t("Вложения"), icon: "huge-file-attach" },
    tariff: {
      label: t("Тарифы"),
      icon: "huge-tariff",
    },
    payment: {
      label: t("Оплата"),
      icon: "huge-document-info",
    },
    sign: {
      label: t("Подписание"),
      icon: "huge-editor-magic-wand",
    },
  };
});

const availableDownloadLanguages = computed(() => {
  const langLabels = { ru: t("Русский"), kz: t("Қазақша"), en: "English" };
  return getAvailableTemplatePayloads().map((p) => ({
    lang: p.lang,
    label: langLabels[p.lang] || p.lang,
  }));
});

const tabComponents = {
  main: ExecutiveInscriptionMain,
  tariff: ExecutiveInscriptionCalculation,
  payment: PaymentTab,
  redactor: ExecutiveInscriptionRedactor,
  attachments: ExecutiveInscriptionAttachments,
  sign: ExecutiveInscriptionSign,
  history: ExecutiveInscriptionHistoryView,
};

const currentTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

const stepperOpen = ref(false);
const expandedStepKey = ref(null);
const savedTariff = ref(null);
const documents = ref([]);
const visitedTabs = ref({});

function markTabVisited(tab) {
  if (!tab || visitedTabs.value[tab]) return;
  visitedTabs.value = { ...visitedTabs.value, [tab]: true };
}

async function fetchSavedTariff() {
  if (!uuid.value || uuid.value === "new") return;
  try {
    const { success, payload } = await getNotarialTariffSaved(uuid.value);
    if (success && payload) {
      savedTariff.value = payload;
    } else {
      savedTariff.value = null;
    }
  } catch (e) {
    console.error("[fetchSavedTariff] failed:", e);
  }
}

async function fetchDocuments() {
  if (!uuid.value || uuid.value === "new") return;
  try {
    const { success, payload } = await getDocumentListNotarial(uuid.value);
    if (success && payload) {
      documents.value = Array.isArray(payload)
        ? payload
        : payload.items || payload.documents || [];
    } else {
      documents.value = [];
    }
  } catch (e) {
    console.error("[fetchDocuments] failed:", e);
  }
}

function onTabRefresh() {
  if (uuid.value && uuid.value !== "new") {
    fetchSavedTariff();
    fetchDocuments();
  }
}

function onPaymentPaid() {
  if (uuid.value && uuid.value !== "new") {
    refreshStatusOnly();
  }
}

watch(
  uuid,
  (val) => {
    if (val && val !== "new") {
      fetchSavedTariff();
      fetchDocuments();
    }
  },
  { immediate: true }
);

function hasContent(value) {
  if (!value) return false;
  if (typeof value === "string") return value.length > 0;
  if (typeof value !== "object") return false;
  if (Array.isArray(value)) return value.length > 0;
  return Object.keys(value).length > 0;
}

const paramsStats = computed(() => {
  const fields = form?.template_fields || {};
  const entries = Object.values(fields);
  const total = entries.length;
  const filled = entries.filter(
    (f) => f && f.value != null && f.value !== ""
  ).length;
  return { total, filled };
});

const doneCreated = computed(() => Boolean(form?.uuid || form?.id));

const doneParams = computed(() => {
  if (visitedTabs.value.main) return true;
  const s = paramsStats.value;
  if (s.total === 0) return true;
  return s.filled === s.total;
});

const doneEditor = computed(
  () => hasContent(form?.content) || hasContent(form?.content_kz)
);

const doneTariff = computed(() => Boolean(savedTariff.value?.id));

const doneAttachments = computed(() => documents.value.length > 0);

const doneSign = computed(
  () =>
    form?.status === StatusCode.SIGNED || form?.status === StatusCode.COMPLETED
);

const stepDefinitions = computed(() => [
  {
    key: "created",
    index: 1,
    title: t("Исп. надпись создана"),
    description: t("Исполнительная надпись зарегистрирована в системе."),
    icon: "huge-add-pen",
    done: doneCreated.value,
    summary: doneCreated.value ? "№ " + (form?.id || form?.uuid) : "",
    details: {
      todo: [t("Шаблон применён"), t("Получен идентификатор записи")],
      doneData: doneCreated.value
        ? [{ label: t("ID"), value: String(form?.id || form?.uuid || "") }]
        : [],
      warning: null,
      actionLabel: null,
      targetTab: null,
    },
  },
  {
    key: "redactor",
    index: 2,
    title: t("Редактор"),
    description: t("Заполните текст документа."),
    icon: "huge-edit",
    done: doneEditor.value && unfilledMentionsCount.value === 0,
    summary:
      unfilledMentionsCount.value > 0
        ? t("Не заполнено полей: ") + unfilledMentionsCount.value
        : doneEditor.value
        ? t("Текст заполнен")
        : "",
    details: {
      todo: [
        t("Проверьте подставленные данные"),
        unfilledMentionsCount.value > 0
          ? t("Заполните незаполненные поля: ") + unfilledMentionsCount.value
          : t("Заполните пустые поля"),
        t("Переключите язык и сверьте обе версии"),
      ],
      doneData:
        doneEditor.value && unfilledMentionsCount.value === 0
          ? [{ label: t("Статус"), value: t("Текст заполнен") }]
          : [],
      warning:
        unfilledMentionsCount.value > 0
          ? t("Осталось заполнить полей: ") +
            unfilledMentionsCount.value +
            ". " +
            t("Заполните их, чтобы продолжить.")
          : null,
      actionLabel: t("Открыть редактор"),
      targetTab: "redactor",
    },
  },
  {
    key: "main",
    index: 3,
    title: t("Параметры"),
    description: t("Укажите параметры исполнительной надписи."),
    icon: "huge-text-config",
    done: doneParams.value,
    summary: doneParams.value
      ? t("Заполнено: ") +
        paramsStats.value.filled +
        "/" +
        paramsStats.value.total
      : "",
    details: {
      todo: [
        t("Заполните обязательные поля шаблона"),
        paramsStats.value.total > 0 &&
        paramsStats.value.filled < paramsStats.value.total
          ? t("Осталось: ") +
            (paramsStats.value.total - paramsStats.value.filled)
          : null,
      ].filter(Boolean),
      doneData: doneParams.value
        ? [
            {
              label: t("Поля"),
              value: paramsStats.value.filled + "/" + paramsStats.value.total,
            },
          ]
        : [],
      warning: null,
      actionLabel: t("Открыть параметры"),
      targetTab: "main",
    },
  },
  {
    key: "attachments",
    index: 4,
    title: t("Вложения"),
    description: t("Прикрепите подтверждающие документы."),
    icon: "huge-file-attach",
    done: doneAttachments.value,
    summary: doneAttachments.value
      ? t("Файлов: ") + documents.value.length
      : "",
    details: {
      todo: [t("Прикрепите PDF-документы")],
      doneData: doneAttachments.value
        ? [
            {
              label: t("Файлов"),
              value: String(documents.value.length),
            },
          ]
        : [],
      warning: null,
      actionLabel: t("Открыть вложения"),
      targetTab: "attachments",
    },
  },
  {
    key: "tariff",
    index: 5,
    title: t("Тарифы"),
    description: t("Выберите тариф и рассчитайте стоимость."),
    icon: "huge-tariff",
    done: doneTariff.value,
    summary: doneTariff.value ? t("Тариф выбран") : "",
    details: {
      todo: [
        t("Выберите подходящий тариф"),
        t("Укажите количество (при необходимости)"),
      ],
      doneData: doneTariff.value
        ? [
            {
              label: t("Тариф"),
              value: savedTariff.value?.name || "",
            },
          ]
        : [],
      warning: null,
      actionLabel: t("Открыть тарифы"),
      targetTab: "tariff",
    },
  },
  {
    key: "sign",
    index: 6,
    title: t("Подписание"),
    description: t("Подпишите документ ЭЦП."),
    icon: "huge-editor-magic-wand",
    done: doneSign.value,
    summary: doneSign.value
      ? getStatusLabel(form?.status) || t("Подписан")
      : "",
    details: {
      todo: [t("Завершите все предыдущие шаги"), t("Нажмите «Подписать ЭЦП»")],
      doneData: doneSign.value
        ? [
            {
              label: t("Статус"),
              value: getStatusLabel(form?.status) || t("Подписан"),
            },
          ]
        : [],
      warning: t("После подписания изменения недоступны."),
      actionLabel: t("Открыть подписание"),
      targetTab: "sign",
    },
  },
]);

const { steps, doneCount, totalSteps, progress } = useStepperState({
  definitions: stepDefinitions,
  activeTab,
});

function onStepperAction(step) {
  if (step?.details?.targetTab) {
    changeTab(step.details.targetTab);
  }
}

watch(
  () => route.query.tab,
  async (tab) => {
    activeTab.value = tab || "redactor";
  },
  { immediate: true }
);

watch(
  () => route.name,
  (newName) => {
    if (uuid.value && uuid.value !== "new") {
      getExecInscriptionTransactionItem(uuid.value);
    }

    if (newName === "exec_inscription_view" && activeTab.value === "sign") {
      activeTab.value = "redactor";
    } else if (
      newName !== "exec_inscription_view" &&
      activeTab.value === "history"
    ) {
      activeTab.value = "redactor";
    }
  }
);

async function fetchTemplateItem(create_inscription = false) {
  let data = await getTemplateItem(template_code.value || form.template_code);
  if (data?.success && data.payload) {
    template.value = data.payload;
    if (template_code.value && create_inscription) {
      await createInscriptionTransaction();
    }
  }
}

async function getExecInscriptionTransactionItem(id, skipTemplate = false) {
  loading.value = true;
  try {
    const data = await fetchExecInscription(id);
    if (data?.success && data.payload && !skipTemplate) {
      await fetchTemplateItem();
    }
  } finally {
    loading.value = false;
    await nextTick();
    captureContentBaseline();
  }
}

function goToEdit() {
  router.push({
    name: "exec_inscription_edit",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

function goToView() {
  router.push({
    name: "exec_inscription_view",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

async function changeTab(tab) {
  if (!(await flow.maybeAutosaveBeforeLeave(tab))) return;

  router.replace({
    query: {
      ...route.query,
      tab,
    },
  });

  activeTab.value = tab;
  markTabVisited(tab);

  if (tab === "redactor" && pendingFields.value.length > 0) {
    const unwatch = watch(
      activeForm,
      (newRef) => {
        if (newRef && typeof newRef.insertFieldsAsMentions === "function") {
          nextTick(() => {
            newRef.insertFieldsAsMentions([...pendingFields.value]);
            pendingFields.value = [];
          });
          unwatch();
        }
      },
      { immediate: true }
    );
  }
}

const flow = useTabFlow({
  tabs,
  activeTab,
  isFormDirty,
  serializeContent,
  savedContentSnapshot,
  canAutosave: () => !execView.value && form?.status === StatusCode.PROJECT,
  buildPayload: () => {
    // eslint-disable-next-line no-unused-vars
    const { _repeatSectionTemplate, ...cleanPayload } = form;
    if (!cleanPayload.contract_amount) {
      cleanPayload.contract_amount = 0;
    }
    return cleanPayload;
  },
  update: (payload) => updateExecInscriptionItem(payload, true),
  changeTab,
  onBlockedNext: () => {
    if (activeTab.value === "redactor" && unfilledMentionsCount.value > 0) {
      stepperOpen.value = true;
      expandedStepKey.value = "redactor";
      return true;
    }
    return false;
  },
  onAutosaveError: () => toast.error(t("Не удалось сохранить изменения")),
});

const { hasPrevTab, hasNextTab, goToPrevTab, tryGoNext } = flow;

async function createInscriptionTransaction() {
  let params = {
    ...form,
    content: template.value.content,
    content_kz: template.value.content_kz,
    content_en: template.value.content_en,
    languages: template.value.languages,
    fields: template.value.fields,
    template_fields: template.value.template_fields,
  };
  let data = await createExecInscription(template.value.code, params);
  if (data?.success) {
    if (data.payload?.id) {
      await doCreateStepRun(data.payload);
      router.push({
        name: "exec_inscription_edit",
        params: { id: data.payload.id },
      });
    }
  }
}

async function submitForm() {
  let payload = await preparePayload();
  if (!payload) {
    console.warn("Nothing to submit");
    return;
  }

  loading.value = true;
  try {
    await doUpdateInscription(payload);
  } finally {
    loading.value = false;
  }
}

async function preparePayload() {
  const activeTabRef = activeForm.value;

  if (!activeTabRef) {
    console.warn("No active tab ref");
    return;
  }

  let payload = null;

  if (typeof activeTabRef.submit === "function") {
    payload = await activeTabRef.submit();
    if (!payload) return null;
  } else {
    payload = form;
  }

  if (!payload.contract_amount) {
    payload.contract_amount = 0;
  }

  // eslint-disable-next-line no-unused-vars
  const { _repeatSectionTemplate, ...cleanPayload } = payload;
  return cleanPayload;
}

async function doUpdateInscription(payload, isNotifyDisabled) {
  const data = await updateExecInscriptionItem(payload, isNotifyDisabled);

  if (isNotifyDisabled) {
    return 0;
  }
  if (data?.success) {
    await fetchExecInscription(uuid.value);
    await nextTick();
    captureContentBaseline();
  }
}

async function doCreateStepRun(notary) {
  let params = {
    notary_transaction_id: notary.id,
    notary_transaction_uuid: notary.uuid,
    template_uuid: template.value?.uuid || form?.template_uuid,
  };
  await createStepRun(params);
}

function handleDownloadCommand(command) {
  const payloads = getAvailableTemplatePayloads();
  if (command !== "all") {
    const hasItem = payloads.some((p) => p.lang === command);
    if (!hasItem) {
      toast.error(t("Контент для данного языка пустой"));
      return;
    }
  }
  if (!payloads.length) {
    toast.error(t("Нет данных для скачивания"));
    return;
  }
  if (command === "all") {
    downloadDocuments();
  } else {
    downloadDocuments(command);
  }
}

async function downloadDocuments(lang) {
  const language = lang || "all";

  const response = await generatePdfFromTipTap(uuid.value, { lang: language });
  if (!response?.data) {
    toast.error(t("Не удалось сформировать PDF"));
    return;
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = createFileName(language);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function handlePrintCommand(command) {
  const payloads = getAvailableTemplatePayloads();
  if (command !== "all") {
    const hasContent = payloads.some((p) => p.lang === command);
    if (!hasContent) {
      toast.error(t("Контент для данного языка пустой"));
      return;
    }
  }
  if (!payloads.length) {
    toast.error(t("Нет данных для печати"));
    return;
  }
  if (command === "all") {
    printDocument();
  } else {
    printDocument(command);
  }
}

async function printDocument(lang) {
  const language = lang || "all";

  const response = await generatePdfFromTipTap(uuid.value, { lang: language });
  if (!response?.data) {
    toast.error(t("Не удалось сформировать PDF"));
    return;
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  };

  document.body.appendChild(iframe);

  const cleanup = () => {
    window.URL.revokeObjectURL(url);
    iframe.remove();
    window.removeEventListener("focus", cleanup);
  };
  window.addEventListener("focus", cleanup, { once: true });
}

function getAvailableTemplatePayloads() {
  const candidates = [
    { lang: "ru", json: form?.content },
    { lang: "kz", json: form?.content_kz },
    { lang: "en", json: form?.content_en },
  ];

  return candidates
    .map((item) => {
      let data = item.json;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (error) {
          console.error("Invalid template JSON", error);
          data = null;
        }
      }
      return { lang: item.lang, data };
    })
    .filter((item) => item.data && item.data.type === "doc");
}

function createFileName(lang) {
  const base = form?.number || form?.uuid || form?.id || t("Документ");
  return `${base}_${lang}.pdf`;
}

function insertFieldCollectedAmount(field) {
  pendingFields.value.push(field);

  nextTick(() => {
    const activeFormRef = activeForm.value;

    if (
      activeTab.value === "redactor" &&
      activeFormRef &&
      typeof activeFormRef.insertFieldsAsMentions === "function"
    ) {
      activeFormRef.insertFieldsAsMentions([...pendingFields.value]);
      pendingFields.value = [];
    }
  });
}

if (template_code.value) {
  loading.value = true;
  fetchTemplateItem(true).finally(() => {
    loading.value = false;
  });
}
if (uuid.value && uuid.value !== "new") {
  getExecInscriptionTransactionItem(uuid.value);
}
ensureStatusDictionary();
</script>

<style scoped lang="scss"></style>
